import { useEffect } from 'react';

// Per-page <head> for a single-page app.
//
// index.html carries one title, description and canonical, written for the
// landing page, because that used to be the only page worth ranking. The blog
// posts each need their own, so this hook swaps them in on mount and puts the
// originals back on unmount. Google renders JavaScript before indexing, so it
// sees the swapped-in values; link-preview scrapers (Facebook, LinkedIn,
// WhatsApp) generally do not, so a post shared on social still shows the
// site-wide Open Graph card. Fixing that properly needs prerendering, which is
// a bigger change than this.

const findOrCreate = (selector, create) => {
    let el = document.head.querySelector(selector);
    if (el) return { el, created: false };
    el = create();
    document.head.appendChild(el);
    return { el, created: true };
};

/**
 * @param {object} seo
 * @param {string} seo.title        <title> and og:title
 * @param {string} seo.description  meta description and og:description
 * @param {string} seo.canonical    absolute URL
 * @param {object|object[]} [seo.jsonLd]  structured data, one script per item. A FAQPage
 *                                  here replaces the site-wide one from index.html
 * @param {boolean} [seo.noindex]   keep the page out of search results
 * @param {boolean} [seo.enabled]    false leaves the head alone (data not here yet)
 */
export const usePageSeo = ({ title, description, canonical, jsonLd, noindex = false, enabled = true }) => {
    useEffect(() => {
        if (!enabled) return undefined;
        const restore = [];

        // api/page.js serves a blog page with its own head already in it, plus
        // the shell's original values here, so leaving the page restores those
        // and not the values found on arrival, which are this page's own.
        // Its structured data is dropped because this hook adds the same again.
        const shell = (typeof window !== 'undefined' && window.__SEO_DEFAULTS) || {};
        document.head.querySelectorAll('script[data-ssr-seo]').forEach((s) => s.remove());

        const previousTitle = shell.title != null ? shell.title : document.title;
        document.title = title;
        restore.push(() => { document.title = previousTitle; });

        const swapAttr = (selector, create, attr, value) => {
            const { el, created } = findOrCreate(selector, create);
            const previous = selector in shell ? shell[selector] : el.getAttribute(attr);
            el.setAttribute(attr, value);
            restore.push(() => {
                if (created || previous == null) el.remove();
                else el.setAttribute(attr, previous);
            });
        };

        swapAttr(
            'meta[name="description"]',
            () => Object.assign(document.createElement('meta'), { name: 'description' }),
            'content',
            description,
        );
        swapAttr(
            'link[rel="canonical"]',
            () => Object.assign(document.createElement('link'), { rel: 'canonical' }),
            'href',
            canonical,
        );

        [['og:title', title], ['og:description', description], ['og:url', canonical]].forEach(([property, value]) => {
            swapAttr(
                `meta[property="${property}"]`,
                () => { const m = document.createElement('meta'); m.setAttribute('property', property); return m; },
                'content',
                value,
            );
        });

        if (noindex) {
            swapAttr(
                'meta[name="robots"]',
                () => Object.assign(document.createElement('meta'), { name: 'robots' }),
                'content',
                'noindex, follow',
            );
        }

        const items = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

        // index.html carries the homepage's FAQPage schema. A page with
        // questions of its own must not also claim those, so it is lifted out
        // for as long as this page is showing.
        if (items.some((item) => item && item['@type'] === 'FAQPage')) {
            document.head.querySelectorAll('script[type="application/ld+json"]').forEach((el) => {
                if (el.hasAttribute('data-ssr-seo') || !el.textContent.includes('"FAQPage"')) return;
                const parent = el.parentNode;
                const next = el.nextSibling;
                el.remove();
                restore.push(() => (next && next.parentNode === parent ? parent.insertBefore(el, next) : parent.appendChild(el)));
            });
        }

        items.forEach((item) => {
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.textContent = JSON.stringify(item);
            document.head.appendChild(script);
            restore.push(() => script.remove());
        });

        return () => restore.reverse().forEach((undo) => undo());
        // jsonLd is a fresh object literal on every render, so it is stringified
        // into the dependency list rather than compared by reference.
    }, [enabled, title, description, canonical, noindex, JSON.stringify(jsonLd)]); // eslint-disable-line react-hooks/exhaustive-deps
};

// Every page other than the landing page is titled "Page topic | Mimique".
export const SITE_NAME = 'Mimique';
export const pageTitle = (topic) => (
    !topic || /\|\s*Mimique$/i.test(topic) ? (topic || SITE_NAME) : `${topic} | ${SITE_NAME}`
);

// Title only, for pages that have no need of the rest of usePageSeo. Puts the
// previous title back on unmount.
export const usePageTitle = (topic) => {
    useEffect(() => {
        if (!topic) return undefined;
        const previous = document.title;
        document.title = pageTitle(topic);
        return () => { document.title = previous; };
    }, [topic]);
};
